import { type BlueprintFeatureSelection } from './blueprint';
import {
	type Corporation,
	getCorporationTag,
	CorporationTag,
	getCorpFullName,
	corporationTag2Corporation,
} from './corporation';
import { type Feature, FeatureType, type FeatureOption } from './feature';
import { type Property, PropertyType } from './property';
import type { ValidationError } from '~/composables/characterValidator';

/**
 * In-fiction Cybercore tag carried by a character (origin, mission,
 * heroic death, ...). Shape is intentionally minimal and open-ended;
 * the full tag-system design will extend this as needed.
 */
export interface CybercoreTag {
	id: string;
	type: 'origin' | 'mission' | 'heroicDeath' | string;
	name: string;
	/** ISO-8601 string. Framework-agnostic — not a Firestore Timestamp. */
	earnedAt?: string;
}

export interface Character {
	id: string;
	name: string;
	origin: string; // Combines race and origin
	legacy: string;
	rank: number;
	/**
	 * Campaign module this character was built for (id of a CampaignModule /
	 * `modules` doc), e.g. 'cold-booting' or 'cybercore-standard'. Drives which
	 * races / legacies / homeworlds are offered in the editor. '' = unset.
	 */
	moduleId?: string;
	creds: number;
	avatarUrl: string;
	imageUrl: string;
	rankUrl: string;
	lastSaved?: string;
	details: LevelDetail[];
	/**
	 * Infrastructure flags like "Invalid". Plain strings. Default [].
	 */
	systemTags: string[];
	/**
	 * In-fiction tags earned in play. Default [].
	 */
	cybercoreTags: CybercoreTag[];
	/**
	 * Only present on `_invalid` sibling docs written by a rejected Foundry
	 * push. Canonical character documents do not carry validation errors.
	 */
	validationErrors?: ValidationError[];
}

export interface LevelDetail {
	features: FeatureOption[];
	properties: Property[];
}

export interface CharacterAttributes {
	agility: number;
	intellect: number;
	perception: number;
	fortitude: number;
	willpower: number;
	allure: number;
	might: number;
	tech: number;
	foresight: number;
	reflex: number;
	persuasion: number;
	harmony: number;
}

export interface CharacterSkills {
	[skillName: string]: number;
}

export interface Feat {
	name: string;
	description: string;
}

export const defaultCharacter = (): Character => {
	return {
		id: '-1',
		name: '',
		origin: '',
		legacy: '',
		moduleId: '',
		avatarUrl: '',
		imageUrl: '',
		rankUrl: '',
		rank: 0,
		creds: 200,
		details: [],
		systemTags: [],
		cybercoreTags: [],
	};
};

export const getRankPropertyIconByType = (type: PropertyType | undefined): string => {
	switch (type) {
		case PropertyType.origin:
			return 'img:/properties/origin.svg';
		case PropertyType.legacy:
			return 'img:/properties/legacy.svg';
		case PropertyType.legacyItem:
			return 'img:/properties/legacy.svg';
		case PropertyType.knack:
			return 'img:/properties/knack.svg';
		case PropertyType.quirk:
			return 'img:/properties/quirks.svg';
		case PropertyType.feat:
			return 'img:/properties/quirks.svg'; // Using quirks.svg as fallback until feats.svg is created
		case PropertyType.talent:
			return 'img:/properties/quirks.svg'; // Using quirks.svg as fallback until talents.svg is created
		case PropertyType.skillBoon:
			return 'img:/properties/skillBoon.svg';
		case PropertyType.homeWorld:
			return 'img:/properties/homeWorld.svg';
		case PropertyType.stats:
			return 'img:/properties/stats.svg';
		case PropertyType.corp:
			return 'img:/properties/corp.svg';
		default:
			return '❓';
	}
};

export const getRankFeatureIconByType = (type: FeatureType): string => {
	switch (type) {
		case FeatureType.origin:
			return 'img:/properties/origin.svg';
		case FeatureType.legacy:
			return 'img:/properties/legacy.svg';
		case FeatureType.legacyItem:
			return 'img:/properties/legacy.svg';
		case FeatureType.knack:
			return 'img:/properties/knack.svg';
		case FeatureType.quirk:
			return 'img:/properties/quirks.svg';
		case FeatureType.feat:
			return 'img:/properties/quirks.svg'; // Using quirks.svg as fallback until feats.svg is created
		case FeatureType.talent:
			return 'img:/properties/quirks.svg'; // Using quirks.svg as fallback until talents.svg is created
		case FeatureType.skillBoon:
			return 'img:/properties/skillBoon.svg';
		case FeatureType.homeWorld:
			return 'img:/properties/homeWorld.svg';
		case FeatureType.stats:
			return 'img:/properties/stats.svg';
		case FeatureType.core:
			return 'img:/properties/corp.svg';
		case FeatureType.corp:
			return 'img:/properties/corp.svg';
		default:
			return '❓';
	}
};

export const getCorporation = (char: Character): Corporation => {
	return getCharacterCorporationAtRank(char, char.rank);
};

export const getCharacterCorporationAtRank = (char: Character, rank: number): Corporation => {
	// Ensure the rank does not exceed the bounds of the details array
	const safeRank = Math.min(rank, char.details.length - 1);

	// Search for the corporation property starting from safeRank down to 0
	for (let i = safeRank; i >= 0; i--) {
		const corpProperty = char.details[i].features.find((feature) => feature.type === FeatureType.corp);

		if (corpProperty) {
			const tag = getCorporationTag(corpProperty.tag);
			if (tag) {
				return corporationTag2Corporation(tag);
			}
		}
	}

	// If no corporation property is found, return the default
	return corporationTag2Corporation(CorporationTag.FOFT);
};


export const isPropertyOptionValid = (
    featureOption: FeatureOption, 
    rank: number, 
    characterRequisites: () => BlueprintFeatureSelection[]
): boolean => {
    const rankRequisites = characterRequisites()[rank];

    const checkProperty = (property: Feature): boolean => {
        if (property.type === featureOption.type) {
            return true;
        }

        // Recursively check sub-properties
        return property.subFeatures.some(subProperty => checkProperty(subProperty));
    };

    return rankRequisites.some(req => checkProperty(req));
};

export const validateCharacterDetails = (
    character: Character, 
    characterRequisites: () => BlueprintFeatureSelection[]
): boolean => {
    return character.details.every((detail, rank) => {
        const rankRequisites = characterRequisites()[rank];

        // Validate each property in the detail
        return detail.features.every(feature => {
            // Find the corresponding requisite for this property
            const req = rankRequisites.find(r => r.type === feature.type);

            // If the requisite is found, validate it and its sub-properties if any
            if (req) {
                if (req.subFeatures.length === 0) {
                    return true; // Valid if there are no sub-properties
                } else {
                    // Check that all sub-properties are present in detail.properties
                    return req.subFeatures.every(subFeat => 
                        detail.features.some(subProp => subProp.type === subFeat.type)
                    );
                }
            }

            return false; // Property type not found in requisites
        });
    });
};